import { FC, useRef } from 'react'
import CoinsSvg from '@/shared/assets/images/Coins.svg'
import useOutsideAlerter from '@/shared/lib/use-outside-alerter'
import { useAppSelector } from '@/shared/lib/redux'
import { selectCommunityInfo } from '@/entities/community'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { createBilling, createEarnApplication } from '@/shared/api/trading'
import { getErrorMessage } from '@/shared/consts/error-codes'
import notify from '@/shared/lib/notify'
import { getBillingPdf } from '@/shared/api/trading/trading'

interface EarnOverlayProps {
    setIsVisible: (arg0: boolean) => void
}

const schema = yup.object().shape({
    value: yup.number().min(10, 'Минимальное пополнение - 10').typeError('Введите число').required('Заполните это поле')
})

interface EarnFormValues {
    value: number
}

const initialState: EarnFormValues = {
    value: 0
}

const EarnOverlay: FC<EarnOverlayProps> = ({ setIsVisible }) => {
    const ref = useRef<HTMLFormElement>(null)

    const { type } = useAppSelector(selectCommunityInfo)
    useOutsideAlerter(ref, () => setIsVisible(false))

    const onSubmit = async (data: EarnFormValues) => {
        await new Promise(async (res, rej) => {
            try {
                if (type === 'foundation') {
                    await createBilling({ amount: data.value }).then(async (data) => {
                        await getBillingPdf(data.data.id).then((resp) => {
                            notify.success('Счет успешно создан')
                            const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
                            const link = document.createElement('a');
                            link.href = url;

                            link.setAttribute(
                                'target',
                                `_blank`,
                            );
                            document.body.appendChild(link);
                            link.click();
                            link.parentNode.removeChild(link);
                            setIsVisible(false)
                            res('')
                        })
                    })
                } else {
                    await createEarnApplication({ amount: data.value }).then(() => {
                        notify.success('Заявка на пополение успешно отправлена')
                        setIsVisible(false)
                        res('')
                    })
                }
                reset()
            } catch (e) {
                notify.error(getErrorMessage(e.response?.data?.error_id))
                rej()
            }
        })
    }

    const {
        register,
        handleSubmit,
        formState: { isSubmitting, errors },
        reset
    } = useForm({
        defaultValues: initialState,
        resolver: yupResolver(schema),
    })

    return (
        <form onSubmit={handleSubmit(onSubmit)} ref={ref} className="flex flex-col gap-3 shadow-md rounded-3xl bg-white p-[10px]">
            <div className={'rounded-3xl max-w-[600px] transition-all ease-in gap-3 bg-color-dark px-4 py-5 screen:px-6 screen:py-7 justify-between flex items-center'}>
                <div className={'text-color-base transition-all ease-in'}>
                    <div className='flex gap-3 justify-between'>
                        <div className={`text-base text-white/70 leading-[16px] screen:text-lg screen:leading-[25px]`}>СУММА ПОПОЛНЕНИЯ</div>
                        {errors['value'] &&
                            <label className="text-sm font-semibold uppercase flex items-center text-color-danger">
                                {errors['value'].message}
                            </label>
                        }
                    </div>
                    <input
                        {...register('value')}
                        className='text-[22px] outline-none placeholder:text-[22px] placeholder:leading-[22px] py-0 appearance-none [&::-webkit-inner-spin-button]:hidden text-white w-full font-mediumttf bg-transparent placeholder:text-white/20 screen:text-[32px] font-semibold'
                        placeholder="ВВЕСТИ СУММУ"
                    />
                </div>
                <div className={`bg-color-primary shrink-0 overflow-hidden transition-all ease-in rounded-full w-[56px] h-[56px] flex items-center justify-center text-color-primary`}>
                    <div className="fill-color-dark text-color-dark">
                        <CoinsSvg width={32} height={32} />
                    </div>
                </div>
            </div>
            <button disabled={isSubmitting} className="w-full rounded-3xl bg-color-primary font-mediumttf text-color-dark text-[25px] px-6 py-8" type="submit">
                {type === 'foundation' ? "СФОРМИРОВАТЬ СЧЕТ" : "ОТПРАВИТЬ ЗАЯВКУ"}
            </button>
        </form>
    )
}

export default EarnOverlay